import React, { useState, useEffect } from "react";
import axios from "axios";
import { Reveal, Eyebrow } from "./primitives";
import { BLOG, SOCIALS, GALLERY, VIDEOS } from "../../lib/siteData";
import { Instagram, Youtube, Facebook, Linkedin, ArrowUpRight, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOC_ICONS = { instagram: Instagram, youtube: Youtube, facebook: Facebook, linkedin: Linkedin };

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  let videoId = null;
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&]+)/);
  const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
  if (watchMatch) videoId = watchMatch[1];
  else if (shortMatch) videoId = shortMatch[1];
  else if (shortsMatch) videoId = shortsMatch[1];
  else if (embedMatch) videoId = embedMatch[1];
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

export const BlogPreview = () => (
  <section className="bg-[#F3F5F4] pt-12 md:pt-16 pb-24 md:pb-32" data-testid="blog-section">
    <div id="blog" className="max-w-[1240px] mx-auto px-5 md:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow>05 — Writing</Eyebrow>
        <h2 className="font-display font-light text-4xl md:text-5xl text-[#161C22] mt-5 leading-tight">
          From the blog
        </h2>
        <p className="text-[#161C22]/70 mt-4 leading-relaxed">
          Plain-language articles on the questions patients ask most often.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-5 mt-14">
        {BLOG.map((b, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <article
              data-testid={`blog-card-${i}`}
              className="hover-lift group h-full rounded-2xl border border-[#DCE3E0] bg-white overflow-hidden hover:border-[#5ECCA0] hover:shadow-xl hover:shadow-[#1F3864]/15"
            >
              <div className="ph-box aspect-[16/10] rounded-none border-x-0 border-t-0">
                <span className="font-mono-ui text-[0.65rem]">Article illustration</span>
              </div>
              <div className="p-6">
                <span className="font-mono-ui text-[0.6rem] uppercase tracking-widest text-[#3E7B5E]">{b.category}</span>
                <h3 className="font-display text-xl text-[#161C22] mt-3 leading-snug">{b.title}</h3>
                <p className="text-sm text-[#161C22]/65 mt-3 leading-relaxed">{b.teaser}</p>
                <span className="inline-flex items-center gap-1 text-sm text-[#1F3864] mt-5 group-hover:gap-2 transition-all">
                  Read article <ArrowUpRight size={15} />
                </span>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const FollowAlong = () => (
  <section className="bg-[#E7ECEA] py-20 md:py-24" data-testid="follow-section">
    <div className="max-w-[1240px] mx-auto px-5 md:px-8">
      <Reveal><Eyebrow>06 — Follow along</Eyebrow></Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {SOCIALS.map((s, i) => {
          const Icon = SOC_ICONS[s.icon];
          return (
            <Reveal key={s.name} delay={i * 0.07}>
              <a
                href="#"
                data-testid={`social-${s.icon}`}
                className="hover-lift flex flex-col gap-4 rounded-2xl border border-[#DCE3E0] bg-white p-6 hover:border-[#5ECCA0]"
              >
                <span className="w-11 h-11 rounded-full bg-[#1F3864] text-white flex items-center justify-center">
                  <Icon size={19} />
                </span>
                <span>
                  <span className="block font-display text-lg text-[#161C22]">{s.name}</span>
                  <span className="block text-sm text-[#161C22]/55 font-mono-ui mt-0.5">{s.handle}</span>
                </span>
              </a>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const Gallery = () => {
  const [uploaded, setUploaded] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gallery`).then((res) => {
      setUploaded(res.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const showUploaded = loaded && uploaded.length > 0;

  return (
    <section id="gallery" className="bg-[#F3F5F4] pt-12 md:pt-16 pb-24 md:pb-32" data-testid="gallery-section">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8">
        <Reveal className="max-w-2xl">
          <Eyebrow>07 — Photo library</Eyebrow>
          <h2 className="font-display font-light text-4xl md:text-5xl text-[#161C22] mt-5 leading-tight">
            Gallery
          </h2>
          <p className="text-[#161C22]/70 mt-4 leading-relaxed">
            Clinic and community environments only — no patient photographs appear anywhere.
          </p>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14">
          {showUploaded ? (
            uploaded.map((item, i) => (
              <Reveal key={item.id} delay={(i % 4) * 0.06}>
                <div
                  data-testid={`gallery-photo-${i}`}
                  className="rounded-2xl overflow-hidden aspect-square border border-[#DCE3E0] hover:border-[#5ECCA0] transition-colors"
                >
                  <img src={item.image_base64} alt={item.label} className="w-full h-full object-cover" />
                </div>
              </Reveal>
            ))
          ) : (
            GALLERY.map((g, i) => (
              <Reveal key={g} delay={(i % 4) * 0.06}>
                <div
                  data-testid={`gallery-slot-${i}`}
                  className="ph-box hover-lift rounded-2xl aspect-square hover:border-[#5ECCA0]"
                >
                  <span className="font-mono-ui text-[0.68rem]">{g}</span>
                </div>
              </Reveal>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export const VideoLibrary = () => {
  const [uploaded, setUploaded] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/videos`).then((res) => {
      setUploaded(res.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const showUploaded = loaded && uploaded.length > 0;

  return (
    <section className="bg-[#E7ECEA] py-24 md:py-32" data-testid="video-section">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8">
        <Reveal className="max-w-2xl">
          <Eyebrow>08 — Video library</Eyebrow>
          <h2 className="font-display font-light text-4xl md:text-5xl text-[#161C22] mt-5 leading-tight">
            Short explainers
          </h2>
          <p className="text-[#161C22]/70 mt-4 leading-relaxed">
            {showUploaded ? "Watch the latest patient education videos." : "Ready to embed YouTube Shorts once produced."}
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5 mt-14">
          {showUploaded ? (
            uploaded.map((item, i) => {
              const embedUrl = getYoutubeEmbedUrl(item.youtube_url);
              return (
                <Reveal key={item.id} delay={i * 0.1}>
                  <div data-testid={`video-embed-${i}`} className="rounded-2xl overflow-hidden border border-[#DCE3E0] bg-[#14263D]">
                    {embedUrl ? (
                      <div className="aspect-[9/16]">
                        <iframe
                          className="w-full h-full"
                          src={embedUrl}
                          title={item.caption}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="aspect-[9/16] flex items-center justify-center p-6">
                        <span className="text-white/40 text-sm font-mono-ui text-center">Invalid YouTube URL</span>
                      </div>
                    )}
                    <div className="p-4">
                      <span className="text-white text-sm">{item.caption}</span>
                    </div>
                  </div>
                </Reveal>
              );
            })
          ) : (
            VIDEOS.map((v, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div
                  data-testid={`video-slot-${i}`}
                  className="hover-lift group relative rounded-2xl overflow-hidden border border-[#DCE3E0] bg-[#14263D] aspect-[9/12] flex flex-col items-center justify-center p-6 hover:border-[#5ECCA0]"
                >
                  <span className="w-16 h-16 rounded-full bg-[#5ECCA0] text-[#14263D] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play size={24} className="ml-1" fill="currentColor" />
                  </span>
                  <span className="font-mono-ui text-xs text-[#5ECCA0] mt-6 text-center leading-relaxed">{v}</span>
                  <span className="absolute top-4 left-4 font-mono-ui text-[0.6rem] uppercase tracking-widest text-white/40">
                    Short · {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
              </Reveal>
            ))
          )}
        </div>
      </div>
    </section>
  );
};
